// Programmatic notification sounds via Web Audio API.
// No external MP3 files — three distinct tones generated on demand:
//   - playOrderNew:    two-note ascending ding (POS register style)
//   - playWaiterCall:  bright bell with long decay (service bell)
//   - playBillRequest: soft descending chime (calm)
//
// Browsers block AudioContext until a user gesture. We expose a one-time
// consent flow: the SoundConsentBanner asks the user to "Activează sunete"
// and that click both resumes the AudioContext and saves consent in
// localStorage so future sessions don't ask again.

const ENABLED_KEY = 'tf_sounds_enabled';
const VOLUME_KEY = 'tf_sounds_volume';
const CONSENT_KEY = 'tf_sounds_consent';

let audioCtx = null;
const lastPlayedAt = {};

function getCtx() {
    if (audioCtx) return audioCtx;
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        audioCtx = new AC();
        return audioCtx;
    } catch {
        return null;
    }
}

export function hasSoundConsent() {
    try { return localStorage.getItem(CONSENT_KEY) === 'true'; } catch { return false; }
}

export function grantSoundConsent() {
    try { localStorage.setItem(CONSENT_KEY, 'true'); } catch {}
    const ctx = getCtx();
    if (ctx && ctx.state === 'suspended') ctx.resume();
}

// Wire a one-shot listener so the first real user gesture (click / key / touch)
// silently grants consent. This is what unlocks AudioContext under the browser
// autoplay policies — no visible dialog required. Calling more than once is a
// no-op (listeners only ever attach once via the module-level guard).
let autoConsentArmed = false;
export function autoGrantConsentOnFirstInteraction() {
    if (autoConsentArmed || hasSoundConsent()) return;
    autoConsentArmed = true;
    const handler = () => {
        grantSoundConsent();
        window.removeEventListener('click', handler, true);
        window.removeEventListener('keydown', handler, true);
        window.removeEventListener('touchstart', handler, true);
    };
    window.addEventListener('click', handler, { capture: true, once: false });
    window.addEventListener('keydown', handler, { capture: true, once: false });
    window.addEventListener('touchstart', handler, { capture: true, once: false });
}

export function isSoundEnabled() {
    try {
        const v = localStorage.getItem(ENABLED_KEY);
        return v === null ? true : v === 'true';
    } catch { return true; }
}

export function setSoundEnabled(v) {
    try { localStorage.setItem(ENABLED_KEY, v ? 'true' : 'false'); } catch {}
}

export function getSoundVolume() {
    try {
        const v = parseFloat(localStorage.getItem(VOLUME_KEY));
        if (isNaN(v)) return 0.6;
        return Math.max(0, Math.min(1, v));
    } catch { return 0.6; }
}

export function setSoundVolume(v) {
    const clamped = Math.max(0, Math.min(1, v));
    try { localStorage.setItem(VOLUME_KEY, String(clamped)); } catch {}
}

// Anti-spam: same sound can't fire more than once per N ms.
function shouldPlay(key, throttleMs = 1500) {
    if (!isSoundEnabled() || !hasSoundConsent()) return false;
    const now = Date.now();
    if (lastPlayedAt[key] && now - lastPlayedAt[key] < throttleMs) return false;
    lastPlayedAt[key] = now;
    return true;
}

// Schedule a single tone with an attack/release envelope. Optionally glides
// from `freq` to `glideTo` over the duration (used for the soft chime).
function scheduleTone(ctx, masterGain, opts) {
    const { freq, type, duration, attack, release, delay = 0, glideTo } = opts;
    const startAt = ctx.currentTime + delay / 1000;
    const endAt = startAt + duration / 1000;

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, startAt);
    if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, endAt);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(1, startAt + attack / 1000);
    gain.gain.exponentialRampToValueAtTime(0.0001, endAt);

    osc.connect(gain).connect(masterGain);
    osc.start(startAt);
    osc.stop(endAt + 0.05);
}

function playSequence(tones) {
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
        try { ctx.resume(); } catch {}
    }
    const master = ctx.createGain();
    master.gain.value = getSoundVolume();
    master.connect(ctx.destination);
    for (const t of tones) scheduleTone(ctx, master, t);
}

// --- Public triggers ---------------------------------------------------------

export function playOrderNew(opts = {}) {
    if (!opts.force && !shouldPlay('order')) return;
    // Two short bright notes — like a POS register confirming a new order.
    playSequence([
        { freq: 880,  type: 'square', duration: 90,  attack: 5, release: 60 },
        { freq: 1320, type: 'square', duration: 130, attack: 5, release: 100, delay: 90 },
    ]);
}

export function playWaiterCall(opts = {}) {
    if (!opts.force && !shouldPlay('waiter')) return;
    // Single bright bell with long decay — concierge service bell.
    playSequence([
        { freq: 1050, type: 'triangle', duration: 650, attack: 3,  release: 500 },
        { freq: 2100, type: 'sine',     duration: 650, attack: 3,  release: 500 },
    ]);
}

export function playBillRequest(opts = {}) {
    if (!opts.force && !shouldPlay('bill')) return;
    // Calm descending sine glide — request, not urgency.
    playSequence([
        { freq: 660, type: 'sine', duration: 450, attack: 20, release: 350, glideTo: 440 },
    ]);
}

// Used by the settings page "Test" buttons — bypasses the spam throttle.
export const SOUND_TRIGGERS = {
    order: playOrderNew,
    waiter: playWaiterCall,
    bill: playBillRequest,
};
